<template>
<div class="potential-info">
    <div class="variation-filter" v-if="potentialData.length > 6 && availableStats.length">
        <span class="filter-label">Filter by</span>
        <span class="stat-toggle"
            v-for="s in availableStats"
            :key="s"
            :class="{ on: statFilter.indexOf(s) !== -1 }"
            v-on:click="toggleStat(s)">{{ statName(s).abbv }}</span>
        <span class="filter-actions">
            <span class="count">{{ potentialData.length }} options</span>
            <button v-on:click.prevent="resetFilterAll">Show all</button>
            <button v-on:click.prevent="resetFilterNone">Show none</button>
        </span>
    </div>

    <div class="variation-grid" :scrollable="potentialData.length > 30">
        <div class="variation-card"
            v-for="d in sortedData"
            :key="d.id"
            :active="potentialId == d.id"
            v-if="!shouldFilter(d) || availableStats.length == 0"
            v-on:click="select(d.id, d.idx)">

            <div class="roll-id" v-if="techMode">#{{ d.id }}:{{ d.potentialNumber }} · {{ d.idx }}</div>

            <stat-grid
                class="variation-stats"
                :statSet="statSet(d)"
                hideHeader="true"
                v-if="!d.skill || d.states.length"></stat-grid>

            <div class="variation-skill" v-if="d.skill">
                <skill-stub :skill-id="d.skill.id" :fill="true" />
            </div>

            <span class="rate-pill dv-chip" v-if="d.rate && !d.skill">{{ d.rate | rate }}%</span>
        </div>
    </div>
</div>
</template>

<script>
import Vue from 'vue';
import ItemIcon from "@/old/game/ItemIcon";
import ItemStat from "@/old/api/item/itemstat";
import StatGrid from "@/old/game/StatGrid";

import Item from "@/old/api/item/item";

Vue.component('item-icon', ItemIcon);
Vue.component('stat-grid', StatGrid);

import SkillStub from "@/components/skill/SkillStub.vue";

export default {
    props: ["potentialData", "selection", "potentialNum"],
    components: {
        SkillStub,
    },
    data: function() {
        return {
            potentialId: 0,
            statFilter: [],
        }
    },
    created() {
        this.init();
    },
    computed: {
        sortedData() {
            let ret = this.potentialData.slice();
            
            ret.sort((a, b) => {
                return a.id - b.id;
            });

            for (let i = 0; i < ret.length; ++i) {
                ret[i].idx = i + 1;
            }

            ret.sort((a, b) => {
                let cmp = b.rate - a.rate;
                if (cmp < 0) {
                    return -1;
                }
                if (cmp > 0) {
                    return 1;
                }

                cmp = a.potentialNumber - b.potentialNumber;
                if (cmp < 0) {
                    return -1;
                }
                if (cmp > 0) {
                    return 1;
                }

                return 0;
            });

            return ret;
        },
        availableStats() {
            let ret = {};
            for (let k in this.potentialData) {
                let v = this.potentialData[k];

                let joined = ItemStat.joinStatSet(v.states, "value");
                // Merge stats that are min/max
                for (let s in joined) {
                    let stat = this.statName(s);
                    if (stat.minmax) {
                        ret[stat.minmax] = true;
                    } else {
                        ret[s] = true;
                    }
                }
            }

            return Object.keys(ret).sort();
        },
        filterAsSet() {
            let ret = {};
            for (let k in this.statFilter) {
                let v = this.statFilter[k];
                ret[v] = true;
            }

            return ret;
        },
        techMode() {
            return this.$store.state.techMode;
        },
    },
    methods: {
        init() {
            if (this.potentialNum) {
                //  Convert this to a potential selection
                let isSet = false;
                for (let k in this.sortedData) {
                    let v = this.potentialData[k];
                    if (v.idx == this.potentialNum) {
                        this.select(v.id, v.idx);
                        isSet = true;
                        break;
                    }
                }

                if (!isSet) {
                    this.select(this.sortedData[0].id, this.sortedData[0].idx);
                }
            } else if (this.selection) {
                //  Check that it exists
                let isSet = false;
                for (let k in this.potentialData) {
                    let v = this.potentialData[k];
                    if (v.id == this.selection) {
                        this.select(v.id, v.idx);
                        isSet = true;
                        break;
                    }
                }

                if (!isSet) {
                    this.select(this.sortedData[0].id, this.sortedData[0].idx);
                }
            } else {
                this.select(this.sortedData[0].id, this.sortedData[0].idx);
            }

            this.resetFilterAll();
        },
        statSet(potential) {
            return ItemStat.joinStatSet(potential.states, "value");
        },
        select(potentialId, index) {
            this.potentialId = potentialId;
            this.$emit("potentialIdChange", this.potentialId, index);
        },
        statName(key) {
            return ItemStat.getStateInfo(key);
        },
        shouldFilter(potential) {
            let states = potential.states;
            for (let k in states) {
                let v = states[k];
                if (this.filterAsSet[v.state]) {
                    return false;
                }

                let stateInfo = ItemStat.getStateInfo(v.state);
                if (stateInfo.minmax && this.filterAsSet[stateInfo.minmax]) {
                    return false;
                }
            }

            return true;
        },
        resetFilterNone() {
            this.statFilter = [];
        },
        resetFilterAll() {
            this.statFilter = this.availableStats.slice();
        },
        toggleStat(s) {
            const i = this.statFilter.indexOf(s);
            if (i === -1) {
                this.statFilter.push(s);
            } else {
                this.statFilter.splice(i, 1);
            }
        },
    }

}
</script>

<style lang="less" scoped>
@import "../../less/core.less";
@import "../../less/common/variables.less";
@import (reference) "../../less/common/wells.less";

.potential-info {
    position: relative;

    .variation-filter {
        position: sticky;
        top: 0;
        z-index: 2;
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 8px;
        margin-bottom: 12px;
        padding: 9px 14px;
        .dv-well();

        .filter-label {
            font-family: @dv-f-geomanist;
            font-size: 11px;
            letter-spacing: 0.18em;
            text-transform: uppercase;
            color: @dv-c-idle;
        }

        .stat-toggle {
            display: inline-block;
            border-radius: @dv-tile-border-radius-circular;
            padding: 2px 11px;
            font-family: @dv-f-geomanist;
            font-size: 12px;
            letter-spacing: 0.05em;
            text-transform: uppercase;
            cursor: pointer;
            border: 1px solid @dv-tile-well-border-color;
            background: @dv-tile-well-bg-color;
            color: @dv-c-idle;
            opacity: 0.55;
            transition: color 0.125s ease-in, border-color 0.125s ease-in,
                background-color 0.125s ease-in, opacity 0.125s ease-in;

            &.on {
                border-color: @dv-c-accent-3;
                background: fade(@dv-c-accent-2, 14%);
                color: @dv-c-foreground;
                opacity: 1;
            }
        }

        .filter-actions {
            margin-left: auto;
            display: flex;
            align-items: center;
            gap: 6px;

            .count {
                font-size: 11px;
                letter-spacing: 0.05em;
                text-transform: uppercase;
                color: @dv-c-idle;
                .margin-right(4px);
            }

            button {
                font-family: @dv-f-geomanist;
                font-size: 11px;
                letter-spacing: 0.08em;
                text-transform: uppercase;
                color: @dv-c-accent-2;
                background: @dv-c-background;
                border: 1px solid @dv-tile-well-border-color;
                border-radius: @dv-tile-border-radius-s;
                padding: 4px 12px;
                cursor: pointer;
            }
        }
    }

    .variation-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
        gap: 10px;
        margin: 10px 0;

        &[scrollable] {
            max-height: 650px;
            overflow-y: auto;
        }
    }

    .variation-card {
        position: relative;
        padding: 10px 12px 12px;
        cursor: pointer;
        .dv-tile();
        border-radius: @dv-tile-border-radius-s;

        &:hover {
            .dv-tile-hover();
        }

        &[active] {
            background: fade(@dv-c-accent-3, 20%);
            border-color: @dv-c-accent-3;
            box-shadow: 0 0 24px fade(@dv-c-accent-2, 18%);
        }

        .roll-id {
            position: absolute;
            top: 8px;
            .right(10px);
            font-family: monospace;
            font-size: 10px;
            letter-spacing: 0.02em;
            color: @dv-c-tech;
            pointer-events: none;
        }

        .variation-stats {
            margin: 2px 0 8px;
            ::v-deep td {
                font-variant-numeric: tabular-nums;
            }
        }

        .variation-skill {
            width: 100%;
        }

        .rate-pill {
            // .dv-chip supplies the pill shape; tint the value
            color: @dv-c-foreground;
            margin: 0;
        }
    }
}
</style>

